import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import GranosCierre from "@/granos/GranosCierre";
import CbotStrip from "@/granos/CbotStrip";
import GranosPosicion from "@/granos/GranosPosicion";
import GranosPrecioDeseado from "@/granos/GranosPrecioDeseado";
import GranosVentas from "./GranosVentas";
import GranosFacturas from "./GranosFacturas";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  LogOut, Loader2, Wheat, ArrowLeft, FileUp, CheckCircle2, FileSignature, Scale,
  Upload, Sheet, ChevronRight, ChevronDown, Search, Trash2, Eye, EyeOff,
  BarChart3, ListChecks, Lock, Menu, X, SlidersHorizontal, Activity, Tag, DollarSign, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { safeLS } from "@/lib/safeStorage";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmtT = (n) => Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtN = (n) => Number(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 });
const miles = (n) => Math.round(Number(n) || 0).toLocaleString("es-AR");
const fmtUSD = (n) => "USD " + miles(n);

const ESTADO_STYLE = {
  "Atrasado": "bg-red-100 text-red-700",
  "En curso": "bg-amber-100 text-amber-700",
  "Pendiente": "bg-amber-100 text-amber-700",
  "Sin firmar": "bg-orange-100 text-orange-700",
  "Cumplido": "bg-emerald-100 text-emerald-700",
  "Sin análisis": "bg-slate-100 text-slate-500",
};
const FIRMA_STYLE = { "Sí": "text-emerald-700", "No": "text-red-600", "Sin dato": "text-slate-400" };

/* ------------------------------------------------------------------ Importar (dialog controlado) */
function GranosImport({ open, setOpen, onDone }) {
  const [cultivo, setCultivo] = useState("__auto__");
  const [fileA, setFileA] = useState(null);
  const [fileB, setFileB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const reset = () => { setFileA(null); setFileB(null); setReview(null); setCultivo("__auto__"); };
  useEffect(() => { if (open) reset(); }, [open]);

  const call = async (confirm) => {
    if (!fileA && !fileB) { toast.error("Subí al menos una planilla."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (fileA) fd.append("file_a", fileA);
      if (fileB) fd.append("file_b", fileB);
      if (cultivo !== "__auto__") fd.append("cultivo", cultivo);
      fd.append("confirm", confirm ? "true" : "false");
      const res = await api.post("/granos/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (confirm) { toast.success(res.data.message || "Importado"); setOpen(false); reset(); onDone?.(); }
      else setReview(res.data.review);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo importar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg" data-testid="granos-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wheat className="h-5 w-5 text-[#14532d]" /> Importar contratos de granos</DialogTitle>
          <DialogDescription>Subí el Listado de Contratos (A) y el Análisis por Depósito (B) de un mismo cultivo y zafra.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <label className="text-xs font-semibold text-slate-500">Cultivo</label>
            <Select value={cultivo} onValueChange={setCultivo}>
              <SelectTrigger className="mt-1 h-9" data-testid="granos-import-cultivo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">Detectar automáticamente</SelectItem>
                <SelectItem value="Soja">Soja</SelectItem>
                <SelectItem value="Maíz">Maíz</SelectItem>
                <SelectItem value="Trigo">Trigo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 rounded-md border p-2 text-sm cursor-pointer hover:bg-slate-50">
            <FileSignature className="h-4 w-4 text-[#14532d]" />
            <span className="flex-1 truncate">{fileA ? fileA.name : "Reporte A · Listado de Contratos"}</span>
            {fileA && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            <input type="file" accept=".xls,.xlsx,.xltx" className="hidden" data-testid="granos-import-file-a" onChange={(e) => setFileA(e.target.files?.[0] || null)} />
          </label>
          <label className="flex items-center gap-2 rounded-md border p-2 text-sm cursor-pointer hover:bg-slate-50">
            <Scale className="h-4 w-4 text-[#14532d]" />
            <span className="flex-1 truncate">{fileB ? fileB.name : "Reporte B · Análisis por Depósito"}</span>
            {fileB && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            <input type="file" accept=".xls,.xlsx,.xltx" className="hidden" data-testid="granos-import-file-b" onChange={(e) => setFileB(e.target.files?.[0] || null)} />
          </label>
          {review && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 text-sm space-y-1" data-testid="granos-import-review">
              <p className="font-semibold text-[#14532d]">Revisión — {review.cultivo} {review.zafra}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[13px]">
                <span>Contratos: <b>{review.total_contratos}</b></span>
                <span>En ambos: <b>{review.en_ambos}</b></span>
                <span>Ton. Listado (A): <b>{fmtT(review.ton_listado_a)}</b></span>
                <span>Ton. Análisis (B): <b>{fmtT(review.ton_analisis_b)}</b></span>
                <span>Solo en A: <b>{review.solo_en_a}</b></span>
                <span>Solo en B: <b>{review.solo_en_b}</b></span>
                <span>Sin firmar: <b>{review.sin_firmar}</b></span>
                <span>Atrasados: <b>{review.atrasados}</b></span>
              </div>
              {review.control_alerta?.length > 0 && (
                <p className="text-amber-700 text-xs">⚠️ Control no cierra: {review.control_alerta.join(", ")}</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy} data-testid="granos-import-cancel">Cancelar</Button>
          {!review ? (
            <Button onClick={() => call(false)} disabled={busy} className="gap-2 bg-[#14532d] hover:bg-[#0f3d21]" data-testid="granos-import-review-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Revisar
            </Button>
          ) : (
            <Button onClick={() => call(true)} disabled={busy} className="gap-2 bg-[#14532d] hover:bg-[#0f3d21]" data-testid="granos-import-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirmar e importar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ Por cultivo / Por tipo + KPIs */
function DashboardTables({ cultivo, vendedor, zafra, incluirFiniquitados, onSinFirmar, onAtrasados }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const p = {};
    if (cultivo && cultivo !== "__all__") p.cultivo = cultivo;
    if (vendedor && vendedor !== "__all__") p.vendedor = vendedor;
    if (zafra && zafra !== "__all__") p.zafra = zafra;
    if (incluirFiniquitados) p.incluir_finiquitados = 1;
    api.get("/granos/dashboard", { params: p }).then((r) => setData(r.data)).catch(() => {});
  }, [cultivo, vendedor, zafra, incluirFiniquitados]);
  if (!data) return null;

  const Card = ({ title, rows }) => (
    <div className="rounded-2xl bg-white overflow-hidden shadow-[0_2px_10px_rgba(20,83,45,0.08)]" data-testid={`granos-dash-${title}`}>
      <div className="bg-[#14532d] text-white px-4 py-2.5 text-sm font-bold">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="text-slate-500 bg-[#f1f4f0]"><tr>
            {["", "Contrat.", "Entreg.", "Pend.", "Wash.", "S/CyP"].map((h, i) => <th key={i} className={`px-3 py-1.5 font-semibold ${i ? "text-right" : "text-left"}`}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.nombre} className="border-t border-slate-100">
                <td className="px-3 py-1.5 font-medium">{r.nombre} <span className="text-slate-400">({r.contratos})</span></td>
                <td className="px-3 py-1.5 text-right">{fmtT(r.contratada)}</td>
                <td className="px-3 py-1.5 text-right">{fmtT(r.entregada)}</td>
                <td className="px-3 py-1.5 text-right font-semibold">{fmtT(r.pendiente)}</td>
                <td className="px-3 py-1.5 text-right">{fmtT(r.washout)}</td>
                <td className="px-3 py-1.5 text-right">{fmtT(r.sin_costo_precio)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <button onClick={onSinFirmar} data-testid="granos-kpi-sinfirmar"
          className="text-left rounded-2xl border-2 border-red-200 bg-red-50 p-4 transition-colors hover:bg-red-100">
          <p className="text-[11px] font-extrabold tracking-wide text-red-700">SIN FIRMAR</p>
          <p className="text-3xl font-extrabold text-red-700 leading-tight">{data.sin_firmar.count}</p>
          <p className="text-xs text-slate-500">{fmtT(data.sin_firmar.toneladas)} t</p>
        </button>
        <button onClick={onAtrasados} data-testid="granos-kpi-atrasados"
          className="text-left rounded-2xl border-2 border-amber-200 bg-amber-50 p-4 transition-colors hover:bg-amber-100">
          <p className="text-[11px] font-extrabold tracking-wide text-amber-700">ATRASADOS</p>
          <p className="text-3xl font-extrabold text-amber-700 leading-tight">{data.atrasados.count}</p>
          <p className="text-xs text-slate-500">{fmtT(data.atrasados.toneladas)} t pend.</p>
        </button>
      </div>
      <Card title="Por cultivo" rows={data.por_cultivo} />
      <Card title="Por tipo de contrato" rows={data.por_tipo} />
    </>
  );
}

/* ------------------------------------------------------------------ Detalle por depósito */
function DepositosRow({ c }) {
  return (
    <tr className="bg-[#f1f4f0]" data-testid="granos-depositos">
      <td colSpan={11} className="px-6 py-2">
        <div className="text-xs text-slate-600">
          <div className="font-semibold mb-1">Detalle por depósito · {c.depositos?.length || 0} · Precio {fmtN(c.precio)} · Costo {fmtN(c.costo)} · {c.observacion || "sin observación"}</div>
          {(c.precio_cerrado != null || c.costo_cerrado != null || c.lugar_entrega) && (
            <div className="mb-2 flex flex-wrap gap-2" data-testid="granos-cierre-info">
              {c.precio_cerrado != null && (
                <span className="rounded-md bg-emerald-100 text-emerald-800 px-2 py-1 font-medium">
                  🔒 Precio cerrado: {fmtN(c.precio_cerrado)} · {fmtT(c.precio_cerrado_tn)} t {c.precio_cerrado_por ? `· ${c.precio_cerrado_por}` : ""}
                </span>
              )}
              {c.costo_cerrado != null && (
                <span className="rounded-md bg-teal-100 text-teal-800 px-2 py-1 font-medium">
                  🔒 Costo cerrado: {fmtN(c.costo_cerrado)} · {fmtT(c.costo_cerrado_tn)} t {c.costo_cerrado_por ? `· ${c.costo_cerrado_por}` : ""}
                </span>
              )}
              {c.lugar_entrega && (
                <span className="rounded-md bg-amber-100 text-amber-800 px-2 py-1 font-medium">📍 {c.lugar_entrega}</span>
              )}
            </div>
          )}
          <table className="w-full">
            <thead className="text-slate-400"><tr>
              {["Depósito", "Contratada", "Entregada", "Pendiente", "Washout", "Sin C/P", "Precio", "Costo", "Situación"].map((h, i) => <th key={i} className={`px-2 py-0.5 ${i ? "text-right" : "text-left"}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {(c.depositos || []).map((d, i) => (
                <tr key={i} className="border-t border-slate-200">
                  <td className="px-2 py-0.5">{d.deposito || "—"}</td>
                  <td className="px-2 py-0.5 text-right">{fmtT(d.contratada)}</td>
                  <td className="px-2 py-0.5 text-right">{fmtT(d.entregada)}</td>
                  <td className="px-2 py-0.5 text-right">{fmtT(d.pendiente)}</td>
                  <td className="px-2 py-0.5 text-right">{fmtT(d.washout)}</td>
                  <td className="px-2 py-0.5 text-right">{fmtT(d.sin_costo_precio)}</td>
                  <td className="px-2 py-0.5 text-right font-medium text-emerald-700">{d.prom_precio ? fmtN(d.prom_precio) : "—"}</td>
                  <td className="px-2 py-0.5 text-right text-slate-600">{d.prom_costo ? fmtN(d.prom_costo) : "—"}</td>
                  <td className="px-2 py-0.5 text-right text-slate-500">{d.situacion || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------ Tarjetas de totales */
function TotalCard({ label, value, unit, accent, children }) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-[0_2px_10px_rgba(20,83,45,0.08)]">
      <div className="text-xs font-semibold text-[#4b6355]">{label}</div>
      <div className={`text-2xl font-extrabold ${accent || "text-[#14231a]"}`}>{value}{unit ? <span className="text-base font-bold"> {unit}</span> : null}</div>
      {children}
    </div>
  );
}

/* ================================================================== MAIN */
export default function GranosSection({ user, onLogout, onBack }) {
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({ cultivos: [], tipos: [], estados: [], condiciones: [], firmados: [], vendedores: [], zafras: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [ocultarCumplidos, setOcultarCumplidos] = useState(false);
  const [incluirFiniquitados, setIncluirFiniquitados] = useState(false);
  const [f, setF] = useState({ zafra: "__all__", cultivo: "__all__", vendedor: "__all__", tipo: "__all__", firmado: "__all__", condicion: "__all__", estado: "__all__" });
  const [productor, setProductor] = useState("");
  const [view, setView] = useState("resumen");
  const [navOpen, setNavOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [moreFilters, setMoreFilters] = useState(false);

  const canAuthorize = user?.role === "admin" || user?.granos_encargado === true;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v !== "__all__") params[k] = v; });
      if (productor.trim()) params.productor = productor.trim();
      if (incluirFiniquitados) params.incluir_finiquitados = 1;
      const res = await api.get("/granos/contratos", { params });
      setRows(res.data.contratos || []);
      setFacets({ cultivos: res.data.cultivos || [], tipos: res.data.tipos || [], estados: res.data.estados || [], condiciones: res.data.condiciones || [], firmados: res.data.firmados || [], vendedores: res.data.vendedores || [], zafras: res.data.zafras || [] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al cargar contratos");
    } finally { setLoading(false); }
  }, [f, productor, incluirFiniquitados]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggle = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setFF = (k) => (v) => setF((o) => ({ ...o, [k]: v }));

  const exportar = () => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v !== "__all__") params.append(k, v); });
    if (productor.trim()) params.append("productor", productor.trim());
    if (incluirFiniquitados) params.append("incluir_finiquitados", "1");
    const token = safeLS.getItem("token");
    fetch(`${API}/granos/export?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = "Granos_Contratos.xlsx"; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => toast.error("No se pudo exportar"));
  };

  const cultivoSel = f.cultivo !== "__all__" ? f.cultivo : null;
  const visibleRows = ocultarCumplidos ? rows.filter((c) => c.estado !== "Cumplido") : rows;
  const cumplidosCount = rows.filter((c) => c.estado === "Cumplido").length;
  const borrar = async () => {
    try {
      const params = cultivoSel ? { cultivo: cultivoSel } : {};
      const res = await api.delete("/granos/contratos", { params });
      toast.success(res.data.message || "Contratos eliminados");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo borrar");
    }
  };

  const tot = useMemo(() => {
    let contr = 0, entr = 0, pend = 0, sinp = 0, costo = 0, precioc = 0;
    for (const c of visibleRows) {
      contr += +c.contratada || 0; entr += +c.entregada || 0; pend += +c.pendiente || 0;
      sinp += +c.sin_costo_precio || 0;
      costo += (+c.costo_cerrado || 0) * (+c.costo_cerrado_tn || 0);
      precioc += (+c.precio_cerrado || 0) * (+c.precio_cerrado_tn || 0);
    }
    return { contr, entr, pend, sinp, costo, precioc, n: visibleRows.length, pct: contr ? Math.round((entr / contr) * 100) : 0 };
  }, [visibleRows]);

  const porCliente = useMemo(() => {
    const m = new Map();
    for (const c of visibleRows) {
      const k = c.productor || c.cliente || "—";
      const o = m.get(k) || { cliente: k, contr: 0, entr: 0, pend: 0, sinp: 0, costo: 0, precioc: 0, n: 0 };
      o.contr += +c.contratada || 0; o.entr += +c.entregada || 0; o.pend += +c.pendiente || 0;
      o.sinp += +c.sin_costo_precio || 0;
      o.costo += (+c.costo_cerrado || 0) * (+c.costo_cerrado_tn || 0);
      o.precioc += (+c.precio_cerrado || 0) * (+c.precio_cerrado_tn || 0);
      o.n += 1;
      m.set(k, o);
    }
    return [...m.values()].map((o) => ({ ...o, pct: o.contr ? Math.round((o.entr / o.contr) * 100) : 0 })).sort((a, b) => b.pend - a.pend);
  }, [visibleRows]);

  const activeMore = ["zafra", "vendedor", "firmado", "condicion", "estado"].filter((k) => f[k] !== "__all__").length
    + (ocultarCumplidos ? 1 : 0) + (incluirFiniquitados ? 1 : 0);

  const goContratosFiltered = (patch) => { setF((o) => ({ ...o, ...patch })); setView("contratos"); setNavOpen(false); };

  /* ---------------- Sidebar ---------------- */
  const NAV = [
    { k: "resumen", label: "Resumen", icon: BarChart3 },
    { k: "posicion", label: "Posición", icon: Activity },
    { k: "precio", label: "Precio deseado", icon: Tag },
    { k: "contratos", label: "Contratos", icon: ListChecks },
    ...(canAuthorize ? [{ k: "ventas", label: "Ventas de granos", icon: DollarSign }] : []),
    ...(canAuthorize ? [{ k: "facturas", label: "Cargar factura", icon: FileText }] : []),
    { k: "importar", label: "Importar", icon: FileUp },
    { k: "cerrar", label: "Cerrar", icon: Lock },
  ];
  const onNav = (k) => {
    if (k === "importar") { setImportOpen(true); setNavOpen(false); return; }
    setView(k); setNavOpen(false);
  };
  const Sidebar = () => (
    <aside className="flex h-full w-64 flex-col bg-[#14532d] text-white">
      <div className="flex items-center gap-2 px-5 pt-5 pb-4">
        <div className="rounded-xl bg-white/10 p-1.5"><Logo className="h-7 w-auto" /></div>
        <div>
          <div className="text-[15px] font-extrabold leading-tight">Tecnomyl Granos</div>
          <div className="text-[11px] text-[#b7d9c2]">Gestión de cartera</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.map(({ k, label, icon: Icon }) => {
          const active = view === k;
          return (
            <button key={k} onClick={() => onNav(k)} data-testid={`granos-nav-${k}`}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold transition-colors ${active ? "bg-white text-[#14532d]" : "text-[#d6e6db] hover:bg-white/10"}`}>
              <Icon className="h-[18px] w-[18px]" /> {label}
            </button>
          );
        })}
      </nav>
      <div className="border-t border-white/10 px-3 py-3 space-y-2">
        <div className="px-2">
          <div className="text-sm font-bold leading-tight truncate">{user?.name || user?.email}</div>
          <div className="text-[11px] text-[#b7d9c2] truncate">{user?.email}</div>
        </div>
        {onBack && (
          <button onClick={onBack} data-testid="granos-back-btn"
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-[#d6e6db] hover:bg-white/10">
            <ArrowLeft className="h-4 w-4" /> Cartera
          </button>
        )}
        <button onClick={onLogout} data-testid="granos-logout-btn"
          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-[#ffd7d2] hover:bg-white/10">
          <LogOut className="h-4 w-4" /> Salir
        </button>
      </div>
    </aside>
  );

  /* ---------------- Toolbar ---------------- */
  const Toolbar = () => (
    <div className="flex flex-wrap items-center gap-2">
      <Button size="sm" onClick={() => setImportOpen(true)} data-testid="granos-import-open" className="gap-2 bg-[#14532d] hover:bg-[#0f3d21]">
        <FileUp className="h-4 w-4" /> Importar planillas
      </Button>
      <Button size="sm" variant="outline" onClick={exportar} disabled={!rows.length} data-testid="granos-export-btn" className="gap-2">
        <Sheet className="h-4 w-4 text-emerald-600" /> Exportar Excel
      </Button>
      {canAuthorize && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" disabled={!rows.length} data-testid="granos-delete-open"
              className="gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
              <Trash2 className="h-4 w-4" /> Borrar
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="granos-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Borrar contratos de Granos</AlertDialogTitle>
              <AlertDialogDescription>
                {cultivoSel
                  ? <>Se eliminarán <b>solo los contratos de «{cultivoSel}»</b>. Esta acción no se puede deshacer. Podés volver a cargarlos importando las planillas.</>
                  : <>Se eliminarán <b>TODOS los contratos de Granos</b> (todos los cultivos). Esta acción no se puede deshacer. Podés volver a cargarlos importando las planillas.</>}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="granos-delete-cancel">Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={borrar} data-testid="granos-delete-confirm" className="bg-red-600 hover:bg-red-700">
                {cultivoSel ? `Borrar ${cultivoSel}` : "Borrar todo"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      <span className="ml-auto rounded-full bg-white px-3 py-1 text-sm font-bold text-[#14532d] shadow-sm" data-testid="granos-count">{tot.n} contrato(s)</span>
    </div>
  );

  /* ---------------- Filtros ---------------- */
  const FSel = ({ k, label, opts }) => (
    <div>
      <label className="mb-1 block text-[11px] font-semibold text-[#4b6355]">{label}</label>
      <Select value={f[k]} onValueChange={setFF(k)}>
        <SelectTrigger className="h-9 bg-white" data-testid={`granos-filter-${k}`}><SelectValue placeholder={label} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Todos</SelectItem>
          {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const Filtros = () => (
    <div className="space-y-3">
      {/* Cliente + cultivo + tipo (siempre visibles) */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative lg:w-72">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input value={productor} onChange={(e) => setProductor(e.target.value)} placeholder="Buscar cliente / productor..."
            className="h-10 w-full rounded-full border border-[#c3cfc2] bg-white pl-9 pr-3 text-sm" data-testid="granos-search-productor" />
        </div>
        <div className="inline-flex gap-1.5 rounded-2xl bg-[#e0e7df] p-1">
          {["__all__", ...facets.cultivos].map((c) => (
            <button key={c} onClick={() => setFF("cultivo")(c)} data-testid={`granos-chip-cultivo-${c}`}
              className={`min-h-9 rounded-xl px-4 text-sm font-bold transition-colors ${f.cultivo === c ? "bg-[#14532d] text-white" : "text-[#14231a]"}`}>
              {c === "__all__" ? "Todos" : c}
            </button>
          ))}
        </div>
        <button onClick={() => setMoreFilters((v) => !v)} data-testid="granos-more-filters"
          className="inline-flex items-center gap-2 rounded-full border border-[#c3cfc2] bg-white px-4 py-2 text-sm font-semibold text-[#14532d]">
          <SlidersHorizontal className="h-4 w-4" /> Más filtros
          {activeMore > 0 && <span className="rounded-full bg-[#14532d] px-1.5 text-xs font-bold text-white">{activeMore}</span>}
          <ChevronDown className={`h-4 w-4 transition-transform ${moreFilters ? "rotate-180" : ""}`} />
        </button>
      </div>
      {/* Tipo de negocio (chips) */}
      <div className="flex flex-wrap gap-2">
        {["__all__", ...facets.tipos].map((t) => (
          <button key={t} onClick={() => setFF("tipo")(t)} data-testid={`granos-chip-tipo-${t}`}
            className={`min-h-9 whitespace-nowrap rounded-full border px-4 text-[13px] font-semibold transition-colors ${f.tipo === t ? "border-[#14532d] bg-[#14532d] text-white" : "border-[#c3cfc2] bg-white text-[#4b6355]"}`}>
            {t === "__all__" ? "Todos" : t}
          </button>
        ))}
      </div>
      {/* Panel Más filtros */}
      {moreFilters && (
        <div className="rounded-2xl border border-[#c3cfc2] bg-white p-4" data-testid="granos-more-panel">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
            <FSel k="zafra" label="Zafra" opts={facets.zafras} />
            <FSel k="vendedor" label="Vendedor" opts={facets.vendedores} />
            <FSel k="firmado" label="Firmado" opts={facets.firmados} />
            <FSel k="condicion" label="Condición" opts={facets.condiciones} />
            <FSel k="estado" label="Estado" opts={facets.estados} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant={ocultarCumplidos ? "default" : "outline"}
              onClick={() => setOcultarCumplidos((v) => !v)} data-testid="granos-toggle-cumplidos"
              className={`gap-2 ${ocultarCumplidos ? "bg-[#14532d] hover:bg-[#0f3d21]" : ""}`}>
              {ocultarCumplidos ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {ocultarCumplidos ? "Cumplidos ocultos" : "Ocultar cumplidos"}
              {cumplidosCount > 0 && <span className="text-xs opacity-70">({cumplidosCount})</span>}
            </Button>
            <Button size="sm" variant={incluirFiniquitados ? "default" : "outline"}
              onClick={() => setIncluirFiniquitados((v) => !v)} data-testid="granos-toggle-finiquitados"
              className={`gap-2 ${incluirFiniquitados ? "bg-[#14532d] hover:bg-[#0f3d21]" : ""}`}>
              {incluirFiniquitados ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              {incluirFiniquitados ? "Mostrando finiquitados" : "Incluir finiquitados"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  /* ---------------- Vistas ---------------- */
  const ResumenView = () => (
    <div className="space-y-5" data-testid="granos-resumen-view">
      <div className="max-w-2xl"><CbotStrip /></div>
      {Filtros()}
      {/* Tarjetas de totales */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <TotalCard label="Entregado" value={`${miles(tot.entr)}`} unit="t" accent="text-[#14532d]">
          <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-[#e0e7df]">
            <div className="h-2.5 rounded-full bg-[#2f9e57]" style={{ width: `${tot.pct}%` }} />
          </div>
          <div className="mt-1 text-[11px] text-[#4b6355]">{tot.pct}% de {miles(tot.contr)} t contratadas</div>
        </TotalCard>
        <TotalCard label="Pendiente de entrega" value={miles(tot.pend)} unit="t" />
        <TotalCard label="Falta cerrar precio" value={miles(tot.sinp)} unit="t" accent="text-[#b45309]" />
        <TotalCard label="Precio cerrado" value={fmtUSD(tot.precioc)} />
        <TotalCard label="Costo cerrado" value={fmtUSD(tot.costo)} />
        <TotalCard label="Contratos" value={tot.n} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Resumen por cliente */}
        <div className="rounded-2xl bg-white p-4 shadow-[0_2px_10px_rgba(20,83,45,0.08)] lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[15px] font-extrabold text-[#14231a]">Resumen por cliente</h3>
            {productor && (
              <button onClick={() => setProductor("")} className="text-xs font-semibold text-[#14532d] underline" data-testid="granos-clear-cliente">Ver todos</button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[#4b6355]"><tr className="border-b border-slate-100">
                {["Cliente", "Contratado", "Entregado", "Pendiente", "Sin precio", "Precio cerr.", "Costo cerr."].map((h, i) => (
                  <th key={i} className={`px-2 py-2 font-bold ${i ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {porCliente.length === 0 ? (
                  <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-400">Sin datos.</td></tr>
                ) : porCliente.map((r) => (
                  <tr key={r.cliente} className="cursor-pointer border-b border-slate-50 hover:bg-[#f1f4f0]"
                    onClick={() => setProductor(r.cliente)} data-testid="granos-cliente-row">
                    <td className="px-2 py-2 font-bold text-[#14231a]">{r.cliente} <span className="font-normal text-slate-400">({r.n})</span></td>
                    <td className="px-2 py-2 text-right">{miles(r.contr)} t</td>
                    <td className="px-2 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-[#e0e7df] sm:block"><div className="h-1.5 bg-[#2f9e57]" style={{ width: `${r.pct}%` }} /></div>
                        <span>{r.pct}%</span>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right font-semibold">{miles(r.pend)} t</td>
                    <td className="px-2 py-2 text-right text-[#b45309]">{miles(r.sinp)} t</td>
                    <td className="px-2 py-2 text-right">{fmtUSD(r.precioc)}</td>
                    <td className="px-2 py-2 text-right">{fmtUSD(r.costo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Tocá un cliente para filtrar todo por él.</p>
        </div>

        {/* KPIs + tablas por cultivo/tipo */}
        <div className="space-y-4">
          <DashboardTables cultivo={f.cultivo} vendedor={f.vendedor} zafra={f.zafra} incluirFiniquitados={incluirFiniquitados}
            onSinFirmar={() => goContratosFiltered({ firmado: "No" })}
            onAtrasados={() => goContratosFiltered({ estado: "Atrasado" })} />
        </div>
      </div>
    </div>
  );

  const ContratosView = () => (
    <div className="space-y-4" data-testid="granos-contratos-view">
      {Filtros()}
      <div className="rounded-2xl bg-white shadow-[0_2px_10px_rgba(20,83,45,0.08)]">
        <div className="max-h-[70vh] overflow-auto rounded-2xl">
          <table className="w-full text-[14px]">
            <thead className="sticky top-0 z-10 bg-[#14532d] text-white">
              <tr>
                {["", "Productor", "Contrato", "Cultivo", "Tipo", "Firma", "Condición", "Contratada", "Pendiente", "% Cump.", "Estado"].map((h, i) => (
                  <th key={i} className={`px-3 py-2.5 font-semibold whitespace-nowrap ${i > 6 && i < 10 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-muted-foreground">Sin contratos. Importá las planillas.</td></tr>
              ) : visibleRows.length === 0 ? (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-muted-foreground">Todos los contratos filtrados están cumplidos. Desactivá «Ocultar cumplidos» para verlos.</td></tr>
              ) : visibleRows.map((c, idx) => {
                const id = `${c.cultivo}-${c.contrato}`;
                const op = expanded.has(id);
                const atrasado = c.estado === "Atrasado";
                return (
                  <Fragment key={id}>
                    <tr onClick={() => toggle(id)} data-testid={`granos-row-${c.contrato}`}
                      className={`cursor-pointer border-t border-slate-100 hover:bg-[#eef3ec] ${atrasado ? "bg-red-50" : idx % 2 ? "bg-[#f7faf6]" : "bg-white"}`}>
                      <td className="px-2 py-2.5">{c.depositos?.length ? (op ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : null}</td>
                      <td className="px-3 py-2.5 font-medium">{c.productor}</td>
                      <td className="px-3 py-2.5 font-mono">{c.contrato}</td>
                      <td className="px-3 py-2.5">{c.cultivo}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap min-w-[190px]">{c.tipo_contrato}</td>
                      <td className={`px-3 py-2.5 font-medium ${FIRMA_STYLE[c.firmado] || ""}`}>{c.firmado}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{c.condicion}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmtT(c.contratada)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{fmtT(c.pendiente)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmtN(c.pct_cumplido)}%</td>
                      <td className="px-3 py-2.5 text-left"><span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold ${ESTADO_STYLE[c.estado] || "bg-slate-100 text-slate-500"}`}>{c.estado}</span></td>
                    </tr>
                    {op && c.depositos?.length > 0 && <DepositosRow key={`${id}-dep`} c={c} />}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#f1f4f0] text-[#14231a]" data-testid="granos-section"
      style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>
      {/* Sidebar desktop */}
      <div className="hidden lg:block lg:sticky lg:top-0 lg:h-screen">
        {Sidebar()}
      </div>
      {/* Sidebar mobile drawer */}
      {navOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setNavOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar()}</div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-[#dde5dc] bg-[#f1f4f0]/90 px-4 py-3 backdrop-blur lg:px-6">
          <button className="lg:hidden" onClick={() => setNavOpen(true)} data-testid="granos-menu-btn"><Menu className="h-6 w-6 text-[#14532d]" /></button>
          <h1 className="text-lg font-extrabold text-[#14532d] lg:text-xl">
            {view === "resumen" ? "Resumen de granos"
              : view === "posicion" ? "Posición del día"
              : view === "precio" ? "Precio deseado"
              : view === "ventas" ? "Ventas de granos"
              : view === "facturas" ? "Cargar factura"
              : view === "contratos" ? "Contratos"
              : "Cerrar precio / costo"}
          </h1>
          <div className="ml-auto hidden md:block">{Toolbar()}</div>
        </header>

        <main className="flex-1 overflow-x-hidden px-4 py-4 lg:px-6 lg:py-6">
          <div className="md:hidden mb-4">{Toolbar()}</div>
          {view === "resumen" && ResumenView()}
          {view === "posicion" && <GranosPosicion setTab={setView} />}
          {view === "precio" && <GranosPrecioDeseado />}
          {view === "ventas" && canAuthorize && <GranosVentas user={user} canAdmin={user?.role === "admin"} />}
          {view === "facturas" && canAuthorize && <GranosFacturas user={user} />}
          {view === "contratos" && ContratosView()}
          {view === "cerrar" && <GranosCierre user={user} canAuthorize={canAuthorize} />}
        </main>
      </div>

      <GranosImport open={importOpen} setOpen={setImportOpen} onDone={load} />
    </div>
  );
}
